import React, { useState, useEffect, useCallback } from 'react'

// Deps
import { connect } from 'react-redux';
import { redirect } from 'controllers/navigator'
import differenceInDays from 'date-fns/differenceInDays';
import parseISO from 'date-fns/parseISO';

// Partials
import Btn from 'views/shared/partials/btn'
import Loader from 'views/shared/partials/loader'
import Pricebox from 'views/shared/partials/pricebox'
import OrderStatus from 'views/shared/partials/order-status'
import Image from 'views/shared/partials/image'
import { Table, TR, TD } from 'views/shared/partials/table'
import { FormInput } from 'views/shared/partials/forms'

// Utils
import { openModal } from 'utils/modals'

// Functions
import parseProducts from 'functions/parse-products'

// Sections
import Breadcrumbs from 'views/admin/sections/breadcrumbs'

// Services
import orderServices from 'services/order'

// Data
import commerceStatusCodes from 'data/commerce-status-codes-detail'

const mapStateToProps = (state, ownProps) => {
	return {
		userData: state.user.data,
	};
};

const CommerceOrderDetail = ({userData, ...props}) => {
	const orderID = props.match.params.id;

	const [data, setData] = useState(false);
	const [commerceStatus, setCommerceStatus] = useState(null);
	const [updatingCommerceStatus, setUpdatingCommerceStatus] = useState(false);

	const getData = useCallback((force) => {
		if(!data || data.business_order_code !== orderID || force === true) {
			setData(false);
			setCommerceStatus(null);

			orderServices.getOrder(orderID).then((payload) => {
				if(payload.is_product_order) {
					setData({
						...payload,
						due_days: differenceInDays(new Date(), parseISO(payload.due_date)),
						parsedProducts: parseProducts(payload.products),
					});
	
					const newStatus = payload.product_order_status ? commerceStatusCodes.find(code => code.value === payload.product_order_status.toString()) : false;
	
					setCommerceStatus(newStatus ? newStatus : null);
				}
				else {
					redirect('orderDetail', { id: payload.business_order_code });
				}
			});
		}

	}, [orderID, data]);

	const updateCommerceStatus = () => {
		setUpdatingCommerceStatus(true);
		orderServices.updateProductStatus(data.business_order_code, commerceStatus.value).then(() => {
			setData({
				...data,
				product_order_status: commerceStatus.value,
			});
		}).catch(() => {
			let revertStatus = data.product_order_status ? commerceStatusCodes.find(code => code.value === data.product_order_status.toString()) : false;
			setCommerceStatus(revertStatus ? revertStatus : null);
		}).finally(() => {
			setUpdatingCommerceStatus(false);
		});
	}

	useEffect(() => {
		getData();
	}, [orderID, getData])

	const addressDelivery = (data && data.product_delivery_method_id !== null) ? data.product_delivery_method_id === 2 : null;

	return (
		<main className="page order-detail">
			<Breadcrumbs
				midSections={[
					{href: 'commerceOrders'}
				]} />
			<section className="portlet section detail-info loader-container">
				<Loader loading={!data} />
				{data &&
					<React.Fragment>
						<div className="portlet-header">
							<h1 className="portlet-title">
								<span>{(data.client.invoice_type === 2 && data.client.company_name) ? data.client.company_name : (data.client.executive_name + ' ' + data.client.executive_surname)}</span>
								{(data.client.invoice_type === 2 && data.client.company_name) &&
									<span className="title-subtitle">{data.client.executive_name} {data.client.executive_surname}</span>
								}
							</h1>

							<div className="header-controls">
								<Btn
									white small wide
									tag="link"
									href="orderDetail"
									params={{ id: data.business_order_code }}
									icon="payment">
									Talep Detayına Git
								</Btn>
								<Btn
									blue small wide
									className="controls-sendlink"
									onClick={() => {
										openModal('order-summary', { orderCode: data.business_order_code, onNotificationSent: () => { getData(true); } })
									}}
									icon="link">
									Link Gönder
								</Btn>
							</div>
						</div>

						<div className="portlet-section">
							<div className="portlet-infobar">
								<div className="grid-container">
									<div className="row">
										<div className="col x3">
											<div className="infobar-section">
												<strong className="section-title">Sipariş Tutarı</strong>
												<span className="section-data">
													<Pricebox
														showCode big
														price={data.total_amount}
														currency={data.price_type.currency_symbol} />
												</span>
											</div>
										</div>
										<div className="col x3">
											<div className="infobar-section">
												<strong className="section-title">Kalan Bakiye</strong>
												<span className="section-data">
													<p>
														<Pricebox
															showCode big
															price={data.remaining_amount}
															currency={data.price_type.currency_symbol} />
													</p>
													{(data.remaining_amount && data.remaining_amount > 0 && data.currency_exchange_type_id !== "0" && data.currency_exchange_rate.rate) &&
														<p>
															<span className="label dark text-regular">
																<Pricebox
																	monochrome
																	price={(data.remaining_amount * data.currency_exchange_rate.rate)} />
															</span>
														</p>
													}
													{data.due_days > 0 &&
														<p>
															<span className="label error">{data.due_days} Gün Gecikti</span>
														</p>
													}
												</span>
											</div>
										</div>
										<div className="col x3">
											<div className="infobar-section">
												<strong className="section-title">Ödenen Bakiye</strong>
												<span className="section-data">
													<p>
														<Pricebox
															big
															price={data.payment_amount}
															currency={data.price_type.currency_symbol} />
													</p>
													{(data.currency_exchange_type_id > 0 && data.currency_exchange_amount && data.currency_exchange_amount !== "0") &&
														<React.Fragment>
															<p className="price-conversion">
																<span className="label dark text-regular">
																	<Pricebox
																		monochrome
																		price={data.currency_exchange_amount} />
																</span>
																<Pricebox
																	monochrome
																	currency={data.price_type.currency_symbol}
																	price={data.currency_amount} />
															</p>
															<p>
																<span className="label dark text-regular">
																	<Pricebox
																		monochrome
																		currency={data.price_type.currency_symbol}
																		price={data.payment_amount - data.currency_amount} />
																</span>
															</p>
														</React.Fragment>
													}
												</span>
											</div>
										</div>
										<div className="col x3">
											<div className="infobar-section">
												<strong className="section-title">İade Edilen Bakiye</strong>
												<span className="section-data">
													<p>
														<Pricebox
															big
															price={data.refund_amount}
															currency={data.price_type.currency_symbol} />
													</p>
													{(data.refund_amount > 0 && data.refund_amount !== "0" && data.currency_exchange_type_id > 0 && data.currency_exchange_rate.rate) &&
														<React.Fragment>
															<p className="price-conversion">
																<span className="label dark text-regular">
																	<Pricebox
																		monochrome
																		price={data.currency_exchange_refund_amount} />
																</span>
																<Pricebox
																	monochrome
																	currency={data.price_type.currency_symbol}
																	price={data.currency_refund_amount} />
															</p>
															<p>
																<span className="label dark text-regular">
																	<Pricebox
																		monochrome
																		currency={data.price_type.currency_symbol}
																		price={data.refund_amount - data.currency_refund_amount} />
																</span>	
															</p>
														</React.Fragment>
													}
												</span>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>

						<div className="portlet-section border-top">
							<div className="grid-container">
								<div className="row">
									<div className="col">
										<div className="alert warning">
											<i className="alert-icon icon-payment big"></i>
											<div className="alert-content">
												İlgili ödeme talebi detayına giderek, ödemeleri detaylı inceleyebilirsiniz.
											</div>
											<div className="alert-controls">
												<Btn
													blue small wide
													tag="link"
													href="orderDetail"
													params={{ id: data.business_order_code }}>
													Ödeme Detayına Git
												</Btn>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>

						<div className="portlet-section border-top">
							<div className="portlet-infobar horizontal">
								<div className="grid-container">
									<div className="row">
										<div className="col x8">
											<strong className="infobar-title">Müşteri Bilgileri</strong>
											<div className="row">
												<div className="col">
													<div className="infobar-section">
														<strong className="section-title">{data.client.invoice_type === 2 ? 'Yetkili' : 'Müşteri'} Adı Soyadı</strong>
														<span className="section-data">
															{data.client.executive_name} {data.client.executive_surname}

															{data.client.invoice_type === 1 ?
																<label className="data-label label dark">
																	Bireysel
																</label>
																:
																<label className="data-label label">
																	Kurumsal
																</label>
															}
														</span>
													</div>
													<div className="infobar-section">
														<strong className="section-title">Cep Telefonu</strong>
														<span className="section-data">
															{data.client.phone_number}
														</span>
													</div>
													<div className="infobar-section">
														<strong className="section-title">E-Posta Adresi</strong>
														<span className="section-data">
															{data.client.email}
														</span>
													</div>
												</div>
											</div>
											{addressDelivery !== null &&
												<>
													<strong className="infobar-title">Teslimat Bilgileri</strong>
													<div className="row">
														<div className="col x6">
															<div className="infobar-section">
																<strong className="section-title">Teslimat Yöntemi</strong>
																<span className="section-data">
																	{addressDelivery ? 'Adrese Teslimat' : <>Mağazada Teslimat: {data.target_group && <strong>{data.target_group.name}</strong>}</>}
																</span>
															</div>
															{addressDelivery &&
																<>
																	<div className="infobar-section">
																		<strong className="section-title">İl</strong>
																		<span className="section-data">
																			{data.client.delivery_city}
																		</span>
																	</div>
																	<div className="infobar-section">
																		<strong className="section-title">İlçe</strong>
																		<span className="section-data">
																			{data.client.delivery_district}
																		</span>
																	</div>
																	<div className="infobar-section">
																		<strong className="section-title">Adres</strong>
																		<span className="section-data">
																			{data.client.delivery_address}
																		</span>
																	</div>
																</>
															}
														</div>
														{addressDelivery &&
															<div className="col x6">
																<div className="infobar-section">
																	<strong className="section-title">Adres İsmi</strong>
																	<span className="section-data">
																		{data.client.delivery_title}
																	</span>
																</div>
																<div className="infobar-section">
																	<strong className="section-title">Telefon Numarası</strong>
																	<span className="section-data">
																		{data.client.delivery_phone}
																	</span>
																</div>
																<div className="infobar-section">
																	<strong className="section-title">Teslimat Yapılacak Kişi</strong>
																	<span className="section-data">
																		{data.client.delivery_owner}
																	</span>
																</div>
															</div>
														}
													</div>
												</>
											}
											<strong className="infobar-title">Fatura Bilgileri</strong>
											<div className="row">
												<div className="col x6">
													<div className="infobar-section">
														<strong className="section-title">Cari Kodu</strong>
														<span className="section-data">
															{data.client.client_code}
														</span>
													</div>
													
													{data.client.invoice_type === 1 ?
														<div className="infobar-section">
															<strong className="section-title">TC Kimlik No</strong>
															<span className="section-data">
																{data.client.identity_number}
															</span>
														</div>
														:
														<div className="infobar-section">
															<strong className="section-title">Vergi No</strong>
															<span className="section-data">
																{data.client.tax_number}
															</span>
														</div>
													}
													<div className="infobar-section">
														<strong className="section-title">Fatura İsmi</strong>
														<span className="section-data">
															{data.client.invoice_name}
														</span>
													</div>
													<div className="infobar-section">
														<strong className="section-title">Fatura Adresi</strong>
														<span className="section-data">
															{data.client.invoice_address}
														</span>
													</div>
												</div>
												<div className="col x6">
													<div className="infobar-section">
														<strong className="section-title">Fatura Ünvanı</strong>
														<span className="section-data">
															{data.client.company_name}
														</span>
													</div>
													{data.client.invoice_type !== 1 &&
														<div className="infobar-section">
															<strong className="section-title">Vergi Dairesi</strong>
															<span className="section-data">
																{data.client.tax_office}
															</span>
														</div>
													}
													<div className="infobar-section">
														<strong className="section-title">İl</strong>
														<span className="section-data">
															{data.client.invoice_city}
														</span>
													</div>
													<div className="infobar-section">
														<strong className="section-title">İlçe</strong>
														<span className="section-data">
															{data.client.invoice_district}
														</span>
													</div>
												</div>
											</div>
										</div>
										<div className="col x4">
											<strong className="infobar-title">Sipariş Bilgileri</strong>
											<div className="row">
												<div className="col x12">
													<div className="infobar-section">
														<strong className="section-title">Firma Sipariş Kodu #</strong>
														<span className="section-data">
															{data.business_order_code}
														</span>
													</div>

													<div className="infobar-section">
														<strong className="section-title">Sipariş Kodu</strong>
														<span className="section-data">
															{data.order_code}
														</span>
													</div>

													<div className="infobar-section">
														<strong className="section-title">İşletme Grubu</strong>
														<span className="section-data">
															{data.group.name}
														</span>
													</div>

													<div className="infobar-section">
														<strong className="section-title">Sipariş Oluşturan</strong>
														<span className="section-data">
															{data.account.name} {data.account.surname}
														</span>
													</div>
													<div className="infobar-section">
														<strong className="section-title">Ödeme Durumu</strong>
														<span className="section-data">
															<OrderStatus label status={data.status} />
														</span>
													</div>
													{/* <div className="infobar-section">
														<strong className="section-title">Sipariş Durumu</strong>
														<span className="section-data">
															<CommerceOrderStatus label status={data.product_order_status} />
														</span>
													</div> */}
												</div>
											</div>
											<div className="row">
												<div className="col flex">
													<FormInput
														disabled={updatingCommerceStatus}
														type="select"
														label="Sipariş Durumu"
														onChange={setCommerceStatus}
														options={commerceStatusCodes}
														value={commerceStatus} />
												</div>
												<div className="col free">
													<Btn
														big blue
														onClick={updateCommerceStatus}
														loading={updatingCommerceStatus}
														disabled={
															updatingCommerceStatus ||
															(!data.product_order_status && !commerceStatus) ||
															(commerceStatus && data.product_order_status && data.product_order_status.toString() === commerceStatus.value)
														}>
														Güncelle
													</Btn>
												</div>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
					</React.Fragment>
				}
			</section>

			{(data && data.parsedProducts && data.parsedProducts.active.length > 0) &&
				<section className="portlet section detail-products">
					<div className="portlet-header">
						<h1 className="portlet-title">
							Ürünler
						</h1>
						{(userData.is_owner && data.parsedProducts.active.length > 0) &&
							<div className="header-controls">
								<Btn
									white small wide
									icon="refresh"
									onClick={() => { openModal('return-order-product', { orderData: data, onSuccess: () => { getData(true); } }); }}>
									İade İşlemleri
								</Btn>
							</div>
						}
					</div>

					<div className="portlet-section">
						<Table
							emptyMessage="Hiç ürün bulunmuyor."
							className="products-table"
							heading={
								<React.Fragment>
									<th>Görsel</th>
									<th>Ürün</th>
									<th>Fiyat</th>
								</React.Fragment>
							}>
							{data.parsedProducts.active.map((product, nth) => (
								<TR key={nth} className="table-product">
									<TD className="product-image">
										<div className="table-imagewrap">
											<Image src={product.product.image_link} />
										</div>
									</TD>
									<TD className="product-description">
										<strong>
											{product.product.title}
										</strong>
										<p><strong>{product.quantity} adet</strong> x <Pricebox monochrome price={product.price_new} /></p>
										<p className="table-subtitle">
											{product.product.mpn}
										</p>
										{addressDelivery &&
											<p className="table-subtitle">
												{product.product.cargo_day} gün içinde kargoya verilir.
											</p>
										}
									</TD>
									<TD className="product-price">
										<Pricebox price={product.total_price} />
									</TD>
								</TR>
							))}
						</Table>
					</div>
				</section>
			}

			{(data && data.parsedProducts && data.parsedProducts.returned.length > 0) &&
				<section className="portlet section detail-products">
					<div className="portlet-header">
						<h1 className="portlet-title">
							İade Edilen Ürünler
						</h1>
					</div>

					<div className="portlet-section">
						<Table
							emptyMessage="Hiç ürün bulunmuyor."
							className="products-table"
							heading={
								<React.Fragment>
									<th>Görsel</th>
									<th>Ürün</th>
									<th>Fiyat</th>
								</React.Fragment>
							}>
							{data.parsedProducts.returned.map((product, nth) => (
								<TR key={nth} className="table-product">
									<TD className="product-image">
										<div className="table-imagewrap">
											<Image src={product.product.image_link} />
										</div>
									</TD>
									<TD className="product-description">
										<strong>
											{product.product.title}
										</strong>
										<p><strong>{product.quantity} adet</strong></p>
										<p>
											<span className="label warning">İade Edildi</span>
										</p>
										<p className="table-subtitle">
											{product.product.product_id}
										</p>
									</TD>
									<TD className="product-price">
										<Pricebox price={product.price_new * product.quantity} />
									</TD>
								</TR>
							))}
						</Table>
					</div>
				</section>
			}
		</main>
	)
}

export default connect(mapStateToProps)(CommerceOrderDetail);